import store from '@/store';

/**
 * @interface Route
 *
 * @param { String } icon - mdi icon
 * @param { String } customIcon - custom svg icons added at '@/icons'
 * @param { String } name - route name
 * @param { String } path - path for route
 * @param { Boolean } hidden - visibility in drawer by style display
 * @param { Boolean } dontExist - visibility in drawer by VNode life cycle
 * @param { Array<Route> } children - children route
 * @param { Boolean } disabled - won't push route when true
 * @param { Boolean } expandable - expandable
 * @param { String } grant - visibility by grant
 */

const originRoutes = [
  {
    icon: '',
    text: 'routes.PAYING_PROCESS',
    name: 'paying-process',
    path: '/paying-process',
    component: () => import('../page/paying-process.vue'),
    hidden: true,
    grant: true
  },
  {
    icon: '',
    text: '',
    name: '',
    path: '/',
    component: () => import('../page/index.vue'),
    redirect: '/overview',
    hidden: true,
    grant: true,
    children: [
      {
        icon: 'mdi-home-variant-outline',
        text: 'routes.HOME',
        name: 'overview',
        path: '/overview',
        grant: 'home.read',
        component: () => import('../page/overview.vue')
      },

      {
        icon: 'mdi-account-details-outline',
        text: 'routes.MERCHANT_MANAGEMENT',
        name: 'merchant-management',
        expandable: true,
        path: '/merchant',
        redirect: '/merchant/list',
        grant: ['merchant_list.read', 'merchant_type_list.read'],
        component: () => import('../page/merchant/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.MERCHANT_LIST',
            name: 'merchant-list',
            path: '/merchant/list',
            grant: 'merchant_list.read',
            component: () => import('../page/merchant/list.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.MERCHANT_CATEGORY',
            name: 'merchant-category',
            path: '/merchant/category',
            grant: 'merchant_type_list.read',
            component: () => import('../page/merchant/category.vue')
          }
        ]
      },

      {
        icon: 'mdi-format-list-bulleted-triangle',
        text: 'routes.COLLECT_MANAGEMENT',
        name: 'collect-management',
        expandable: true,
        path: '/collect',
        redirect: '/collect/order',
        grant: 'collect_order_list.read',
        component: () => import('../page/collect/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.COLLECT_ORDER',
            name: 'collect-order',
            path: '/collect/order',
            grant: 'collect_order_list.read',
            component: () => import('../page/collect/order.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.COLLECT_NOT_PROCESSED',
            name: 'collect-not-processed',
            path: '/collect/not-processed',
            grant: 'not_process_collect_order_list.read',
            component: () => import('../page/collect/not-processed.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.COLLECT_BLACK_LIST',
            name: 'collect-black-list',
            path: '/collect/black-list',
            grant: 'black_list_collect_order_list.read',
            component: () => import('../page/collect/black-list.vue')
          }
        ]
      },

      {
        icon: 'mdi-format-list-bulleted-type',
        text: 'routes.PAY_MANAGEMENT',
        name: 'pay-management',
        expandable: true,
        path: '/pay',
        redirect: '/pay/merchant-list',
        grant: ['pay_order_list.read', 'api_pay_order_list.read'],
        component: () => import('../page/pay/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.PAY_MERCHANT_LIST',
            name: 'pay-merchant-list',
            path: '/pay/merchant-list',
            grant: 'pay_order_list.read',
            component: () => import('../page/pay/merchant-list.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.PAY_API_LIST',
            name: 'pay-api-list',
            path: '/pay/api-list',
            grant: 'api_pay_order_list.read',
            component: () => import('../page/pay/api-list.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.PAY_BLACK_LIST',
            name: 'pay-black-list',
            path: '/pay/black-list',
            grant: 'black_list_pay_order_list.read',
            component: () => import('../page/pay/black-list.vue')
          }
        ]
      },

      {
        icon: 'mdi-currency-usd',
        text: 'routes.FUNDING_MANAGEMENT',
        name: 'funding-management',
        expandable: true,
        path: '/funding',
        redirect: '/funding/history',
        grant: [
          'capital_log_list.read',
          'collect_orders_leaderboard.read',
          'frozen_log_list.read'
        ],
        component: () => import('../page/funding/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.FUNDING_HISTORY',
            name: 'funding-history',
            path: '/funding/history',
            grant: 'capital_log_list.read',
            component: () => import('../page/funding/history.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.FUNDING_RANKING',
            name: 'funding-ranking',
            path: '/funding/ranking',
            grant: 'collect_orders_leaderboard.read',
            component: () => import('../page/funding/ranking.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.FUNDING_FROZEN',
            name: 'funding-frozen',
            path: '/funding/frozen',
            grant: 'frozen_log_list.read',
            component: () => import('../page/funding/frozen.vue')
          }
        ]
      },

      {
        icon: 'mdi-tune-vertical-variant',
        text: 'routes.RULE_MANAGEMENT',
        name: 'rule-management',
        expandable: true,
        path: '/rule',
        redirect: '/rule/collect',
        grant: ['collect_rule.read', 'pay_rule.read'],
        component: () => import('../page/rule/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.RULE_COLLECT',
            name: 'rule-collect',
            path: '/rule/collect',
            grant: 'collect_rule.read',
            component: () => import('../page/rule/collect.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.RULE_PAY',
            name: 'rule-pay',
            path: '/rule/pay',
            grant: 'pay_rule.read',
            component: () => import('../page/rule/pay.vue')
          }
        ]
      },

      {
        icon: 'mdi-transit-detour',
        text: 'routes.CHANNEL_MANAGEMENT',
        name: 'channel-management',
        expandable: true,
        path: '/channel',
        redirect: '/channel/list',
        grant: [
          'collect_channel_settings.read',
          'cards_list.read'
          // 'platform_billing.read',
          // 'pay_channel_settings.read',
        ],
        component: () => import('../page/channel/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.CHANNEL_LIST',
            name: 'channel-list',
            path: '/channel/list',
            grant: 'collect_channel_settings.read',
            component: () => import('../page/channel/list.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.CHANNEL_ACCOUNTS',
            name: 'channel-accounts',
            path: '/channel/accounts',
            grant: 'cards_list.read',
            component: () => import('../page/channel/accounts.vue')
          }
          // {
          //   icon: 'mdi-circle-outline',
          //   text: '平台使用計費',
          //   name: '平台使用計費',
          //   path: '/channel/pricing',
          //   grant: 'platform_billing.read',
          //   component: () => import('../page/channel/pricing.vue')
          // },
          // {
          //   icon: 'mdi-circle-outline',
          //   text: '轉單通道設定',
          //   name: '轉單通道設定',
          //   path: '/channel/transfer',
          //   grant: 'pay_channel_settings.read',
          //   component: () => import('../page/channel/transfer.vue')
          // }
        ]
      },

      {
        icon: 'mdi-chart-line',
        text: 'routes.STATISTICS',
        name: 'statistics',
        expandable: true,
        path: '/statistics',
        redirect: '/statistics/sum',
        grant: [
          'platform_order_reports.read',
          'merchant_accounting_reports.read',
          'agent_accounting_reports.read',
          'channel_accounting_reports.read'
        ],
        component: () => import('../page/statistics/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.STATISTICS_SUM',
            name: 'statistics-sum',
            path: '/statistics/sum',
            grant: 'platform_order_reports.read',
            component: () => import('../page/statistics/sum.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.STATISTICS_MERCHANT',
            name: 'statistics-merchant',
            path: '/statistics/merchant',
            grant: 'merchant_accounting_reports.read',
            component: () => import('../page/statistics/merchant.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.STATISTICS_PROXY',
            name: 'statistics-proxy',
            path: '/statistics/proxy',
            grant: 'agent_accounting_reports.read',
            component: () => import('../page/statistics/proxy.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.STATISTICS_CHANNEL',
            name: 'statistics-channel',
            path: '/statistics/channel-type',
            grant: 'channel_accounting_reports.read',
            showPath: true,
            component: () => import('../page/statistics/channel.vue'),
            children: [
              {
                icon: 'mdi-circle-outline',
                text: 'routes.STATISTICS_SUB_CHANNEL',
                name: 'statistics-sub-channel',
                path: '/statistics/channel-type/:channel_type_id',
                grant: 'channel_accounting_reports.read',
                hidden: true,
                component: () => import('../page/statistics/_channel_type.vue'),
                children: [
                  {
                    icon: 'mdi-circle-outline',
                    text: 'routes.STATISTICS_SUB_ACCOUNT',
                    name: 'statistics-sub-account',
                    path: '/statistics/channel-type/:channel_type_id/:channel_id',
                    grant: 'channel_accounting_reports.read',
                    hidden: true,
                    component: () =>
                      import('../page/statistics/_sub_account.vue')
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },

  {
    icon: '',
    text: 'routes.SYSTEM',
    name: 'system',
    path: '/system',
    redirect: '/system/system-log',
    grant: true,
    component: () => import('../page/system.vue'),
    children: [
      {
        icon: 'mdi-book-open-outline',
        text: 'routes.SYSTEM_LOG',
        name: 'system-log',
        path: '/system/system-log',
        redirect: '/system/system-log/sms',
        expandable: true,
        grant: [
          'sms_list.read',
          'signin_log_list.read'
          // 'operation_log_list.read',
          // 'backup_logs.read',
        ],
        component: () => import('../page/system-log/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.LOG_SMS',
            name: 'log-sms',
            path: '/system/system-log/sms',
            grant: 'sms_list.read',
            component: () => import('../page/system-log/sms.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.LOG_LOGINS',
            name: 'log-logins',
            path: '/system/system-log/logins',
            grant: 'signin_log_list.read',
            component: () => import('../page/system-log/logins.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.LOG_OPERATION',
            name: 'log-operation',
            path: '/system/system-log/operation',
            grant: 'operation_log_list.read',
            component: () => import('../page/system-log/operation.vue')
          }
          // ,{
          //   icon: 'mdi-circle-outline',
          //   text: '日誌備份',
          //   name: '日誌備份',
          //   path: '/system/system-log/backups',
          //   grant: 'backup_logs.read',
          //   component: () => import('../page/system-log/backups.vue')
          // }
        ]
      },
      {
        icon: 'mdi-circle',
        text: 'routes.SYSTEM_MANAGEMENT',
        name: 'system-management',
        path: '/system/system-management',
        redirect: 'system-management/managers',
        expandable: true,
        grant: ['user_list.read', 'role_list.read', 'menu_settings.read'],
        component: () => import('../page/system-management/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.MANAGERS',
            name: 'managers',
            path: '/system/system-management/managers',
            grant: 'user_list.read',
            component: () => import('../page/system-management/managers.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.ROLES',
            name: 'roles',
            path: '/system/system-management/roles',
            grant: 'role_list.read',
            component: () => import('../page/system-management/roles.vue')
          }
          // {
          //   icon: 'mdi-circle-outline',
          //   text: '角色設定',
          //   name: '角色設定',
          //   path: '/system/system-management/roles',
          //   grant: 'menu_settings.read',
          //   component: () => import('../page/system-management/roles.vue')
          // },
        ]
      },
      {
        icon: 'mdi-circle',
        text: 'routes.SYSTEM_CONFIG',
        name: 'system-config',
        path: '/system/system-config',
        redirect: 'system-config/app-config',
        expandable: true,
        grant: [
          'platform_settings.read',
          'password_settings.read',
          'clear_cache.read',
          'system_version.read'
        ],
        component: () => import('../page/system-config/index.vue'),
        children: [
          {
            icon: 'mdi-circle-outline',
            text: 'routes.APP_CONFIG',
            name: 'app-config',
            path: '/system/system-config/app-config',
            grant: 'platform_settings.read',
            component: () => import('../page/system-config/app-config.vue')
          },
          {
            icon: 'mdi-circle-outline',
            text: 'routes.PASSWORD_CHANGE',
            name: 'password-change',
            path: '/system/system-config/password-change',
            grant: 'password_settings.read',
            component: () => import('../page/system-config/password-change.vue')
          }
          // {
          //   icon: 'mdi-circle-outline',
          //   text: '清理暫存',
          //   name: '清理暫存',
          //   path: '/system/system-config/cache-clean',
          //   grant: 'clear_cache.read',
          //   component: () => import('../page/system-config/cache-clean.vue')
          // },
          // {
          //   icon: 'mdi-circle-outline',
          //   text: '系統版本',
          //   name: '系統版本',
          //   path: '/system/system-config/version',
          //   grant: 'system_version.read',
          //   component: () => import('../page/system-config/version.vue')
          // }
        ]
      }
    ]
  }
];

export const filterByGrant = () => {
  const grants = store.getters['auth/getGrants'];

  const getGrantValue = (grantName) => {
    if (Array.isArray(grantName)) {
      const grantNames = grantName;
      return grantNames.some((_grantName) => {
        const grantSplit = _grantName.split('.');
        const name = grantSplit[0];
        const read = grantSplit[1];

        return grants[name][read];
      });
    } else if (typeof grantName === 'string') {
      const grantSplit = grantName.split('.');
      const name = grantSplit[0];
      const read = grantSplit[1];

      return grants[name][read];
    } else if (grantName === true) {
      return true;
    }
  };

  const recuresiveMap = (routes = originRoutes) => {
    return routes
      .map((_route) => {
        if (getGrantValue(_route.grant)) {
          if (_route.children && _route.children.length) {
            _route.children = recuresiveMap(_route.children);
          }

          return _route;
        }
      })
      .filter((v) => !!v);
  };

  return recuresiveMap();
};

export const routes = () =>
  process.env.VUE_APP_SHOW_GLOBAL_COMPONENT
    ? [
        ...filterByGrant(),
        {
          icon: 'mdi-scent',
          text: 'GlobalComponents',
          name: 'GlobalComponents',
          path: '/dev/global-components',
          component: () => import('../components/Globals/index.vue')
        }
      ]
    : filterByGrant();
